import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import { C2, C2_FONT, C2Switch } from "../Table/C2UserTable";
import { c2PrimaryBtnSx, c2SecondaryBtnSx, c2TextFieldSx } from "../conecta2ModalStyles";

export const settingsFieldSx = c2TextFieldSx;

const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Campos de `draft` que difieren de `base` (listos para un `update` de RTDB). */
export const diffObjects = (base, draft) => {
  const changes = {};
  if (!draft) return changes;
  Object.keys(draft).forEach((key) => {
    if (!sameValue(draft[key], base ? base[key] : undefined)) {
      changes[key] = draft[key] === undefined ? null : draft[key];
    }
  });
  return changes;
};

/**
 * Borrador editable de un objeto que llega en tiempo real. Si el origen cambia mientras
 * hay ediciones, se conservan las ediciones y se actualiza el resto de campos.
 */
export function useSettingsDraft(source) {
  const baseRef = useRef(null);
  const [base, setBase] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!source) return;
    const prevBase = baseRef.current;
    setDraft((prev) => (prev && prevBase ? { ...source, ...diffObjects(prevBase, prev) } : source));
    baseRef.current = source;
    setBase(source);
  }, [source]);

  const changes = useMemo(() => diffObjects(base, draft), [base, draft]);
  const dirty = Object.keys(changes).length > 0;
  const setField = useCallback((name, value) => setDraft((d) => ({ ...(d || {}), [name]: value })), []);
  const discard = useCallback(() => setDraft(baseRef.current), []);

  return { draft, setDraft, setField, changes, dirty, discard };
}

export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
export const isUrl = (v) => /^https?:\/\/[^\s.]+\.[^\s]{2,}$/i.test(String(v || "").trim());
export const isNonNegativeNumber = (v) => v !== "" && v !== null && v !== undefined && !isNaN(Number(v)) && Number(v) >= 0;

/** "5000, 10000,20000" → válido si todos son montos positivos. */
export const isAmountList = (v) => {
  const s = String(v || "").trim();
  if (!s) return true;
  return s.split(",").every((p) => p.trim() !== "" && !isNaN(Number(p)) && Number(p) > 0);
};
export const normalizeAmountList = (v) =>
  String(v || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .join(",");

export function SettingsCard({ icon: Icon, title, subtitle, children, sx }) {
  return (
    <Box
      sx={{
        backgroundColor: C2.white,
        borderRadius: "16px",
        border: `1px solid ${C2.border}`,
        boxShadow: "0 8px 24px rgba(26,15,59,0.06)",
        p: { xs: 2, sm: 2.5 },
        height: "100%",
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
        {Icon ? (
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "12px",
              backgroundColor: C2.lavender,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: C2.violet, fontSize: 22 }} />
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: "1rem", color: C2.sidebar }}>{title}</Typography>
          {subtitle ? (
            <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.84rem", color: C2.muted, mt: 0.25, lineHeight: 1.4 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {children}
    </Box>
  );
}

export function SettingToggle({ name, label, description, checked, onChange, disabled }) {
  return (
    <Box
      component="label"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        py: 1.25,
        borderTop: `1px solid ${C2.cellBorder}`,
        cursor: disabled ? "default" : "pointer",
        "&:first-of-type": { borderTop: "none" },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: C2_FONT, fontWeight: 600, fontSize: "0.92rem", color: disabled ? C2.muted : C2.dark }}>
          {label}
        </Typography>
        {description ? (
          <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.8rem", color: C2.muted, lineHeight: 1.4 }}>{description}</Typography>
        ) : null}
      </Box>
      <C2Switch name={name} checked={!!checked} onChange={(e) => onChange(name, e.target.checked)} disabled={disabled} />
    </Box>
  );
}

export function SettingsField({ name, label, value, onChange, error, helper, sx, ...rest }) {
  return (
    <TextField
      fullWidth
      margin="dense"
      variant="outlined"
      id={name}
      name={name}
      label={label}
      value={value ?? ""}
      onChange={(e) => onChange(name, e.target.value)}
      error={!!error}
      helperText={error || helper || " "}
      FormHelperTextProps={{ sx: { fontFamily: C2_FONT, mx: 0.5 } }}
      sx={{ ...settingsFieldSx, ...sx }}
      {...rest}
    />
  );
}

export function DemoModeAlert() {
  return (
    <Alert severity="info" sx={{ mb: 2, borderRadius: "12px", fontFamily: C2_FONT }}>
      Modo demostración: puedes revisar la configuración, pero los cambios no se guardarán.
    </Alert>
  );
}

export function SettingsNotice({ notice, onClose }) {
  if (!notice) return null;
  return (
    <Alert severity={notice.severity} onClose={onClose} sx={{ mb: 2, borderRadius: "12px", fontFamily: C2_FONT }}>
      {notice.message}
    </Alert>
  );
}

export function SettingsSaveBar({ dirty, saving, onSave, onDiscard, disabled, saveLabel = "Guardar cambios", alwaysEnabled = false }) {
  const canSave = (dirty || alwaysEnabled) && !saving && !disabled;
  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 12,
        zIndex: 5,
        mt: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1.5,
        px: { xs: 2, sm: 2.5 },
        py: 1.5,
        borderRadius: "16px",
        backgroundColor: dirty ? C2.dark : C2.white,
        border: `1px solid ${dirty ? C2.dark : C2.border}`,
        boxShadow: dirty ? "0 12px 32px rgba(26,15,59,0.28)" : "0 6px 18px rgba(26,15,59,0.05)",
        transition: "background-color 0.2s, box-shadow 0.2s",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {dirty ? (
          <EditNoteRoundedIcon sx={{ color: "#C9B6FF" }} />
        ) : (
          <CheckCircleOutlineRoundedIcon sx={{ color: "#16A34A" }} />
        )}
        <Typography sx={{ fontFamily: C2_FONT, fontWeight: 600, fontSize: "0.9rem", color: dirty ? C2.white : C2.muted }}>
          {dirty ? "Tienes cambios sin guardar" : "Todo está guardado"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        {onDiscard ? (
          <Button
            variant="outlined"
            onClick={onDiscard}
            disabled={!dirty || saving}
            sx={{
              ...c2SecondaryBtnSx,
              ...(dirty ? { color: C2.white, borderColor: "rgba(251,250,254,0.35)", "&:hover": { borderColor: C2.white, backgroundColor: "rgba(251,250,254,0.08)" } } : {}),
            }}
          >
            Descartar
          </Button>
        ) : null}
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: C2.white }} /> : null}
          sx={{ ...c2PrimaryBtnSx, "&.Mui-disabled": { background: C2.lavender, color: C2.muted, boxShadow: "none" } }}
        >
          {saving ? "Guardando…" : saveLabel}
        </Button>
      </Box>
    </Box>
  );
}
